import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { COMPANY } from "@/i18n/translations";
import { useLang } from "@/context/LanguageContext";
import { MapPin, Phone, Mail, MessageCircle, Globe, Navigation } from "lucide-react";

const MAPS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=DNA+Automotive+GmbH,+Butzweilerstra%C3%9Fe+35-39,+50829+K%C3%B6ln";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-obsidian border-t border-white/10 pt-20 pb-10 px-6 md:px-10" data-testid="footer">
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Logo />
          <p className="text-zinc-400 text-sm mt-6 max-w-xs leading-relaxed">
            {COMPANY.location} · {COMPANY.name}. Exklusive Fahrzeuge, Ankauf, Verkauf & Premium-Service in Köln.
          </p>
          <div className="flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.2em] text-zinc-500">
            <MapPin className="w-3.5 h-3.5 text-brand-bright shrink-0" />
            <span>{COMPANY.location}</span>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.25em] text-brand-bright mb-5">{t.footer.nav}</h4>
          <ul className="space-y-2.5 text-sm text-zinc-300">
            <li><Link to="/fahrzeuge" className="hover:text-brand-bright transition-colors">{t.nav.vehicles}</Link></li>
            <li><Link to="/auto-abo" className="hover:text-brand-bright transition-colors">{t.nav.abo}</Link></li>
            <li><Link to="/finanzierung" className="hover:text-brand-bright transition-colors">{t.nav.financing}</Link></li>
            <li><Link to="/leistungen" className="hover:text-brand-bright transition-colors" data-testid="footer-leistungen">{t.nav.services}</Link></li>
            <li><Link to="/kontakt" className="hover:text-brand-bright transition-colors" data-testid="footer-kontakt">{t.nav.contact}</Link></li>
            <li><Link to="/karriere" className="hover:text-brand-bright transition-colors" data-testid="footer-karriere">{t.nav.career}</Link></li>
            <li className="pt-3 border-t border-white/5"><Link to="/impressum" className="text-zinc-500 hover:text-brand-bright transition-colors" data-testid="footer-impressum">{t.footer.imprint}</Link></li>
            <li><Link to="/agb" className="text-zinc-500 hover:text-brand-bright transition-colors" data-testid="footer-agb">AGB</Link></li>
            <li><Link to="/widerruf" className="text-zinc-500 hover:text-brand-bright transition-colors" data-testid="footer-widerruf">Widerruf</Link></li>
            <li><Link to="/datenschutz" className="text-zinc-500 hover:text-brand-bright transition-colors" data-testid="footer-datenschutz">{t.footer.privacy}</Link></li>
            <li>
              <button
                onClick={() => window.dispatchEvent(new Event("dna:cookie-settings"))}
                className="text-zinc-500 hover:text-brand-bright transition-colors"
                data-testid="footer-cookie-settings"
              >
                {t.footer.cookies}
              </button>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs uppercase tracking-[0.25em] text-brand-bright mb-5">{t.nav.contact}</h4>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-brand-bright mt-0.5 shrink-0" />
              <span>{COMPANY.name}<br />{COMPANY.addressLine}, {COMPANY.city}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-brand-bright shrink-0" />
              <a href={`tel:${COMPANY.phoneHref}`} className="hover:text-brand-bright transition-colors">{COMPANY.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-brand-bright shrink-0" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-brand-bright transition-colors">{COMPANY.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-brand-bright shrink-0" />
              <a href="https://dna-automotive.de" target="_blank" rel="noopener noreferrer" className="hover:text-brand-bright transition-colors">dna-automotive.de</a>
            </li>
            <li className="flex items-center gap-3">
              <MessageCircle className="w-4 h-4 text-brand-bright shrink-0" />
              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-brand-bright transition-colors"
                data-testid="footer-whatsapp"
              >
                WhatsApp
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Navigation className="w-4 h-4 text-brand-bright shrink-0" />
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-brand-bright transition-colors"
                data-testid="footer-route"
              >
                {t.contact.route}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-zinc-500">
        <span>© {new Date().getFullYear()} {COMPANY.name}. {t.footer.rights}</span>
        <span className="uppercase tracking-[0.25em]">{COMPANY.tagline}</span>
      </div>
    </footer>
  );
}
